import axios, { AxiosInstance } from "axios";
import { saveAs } from "file-saver";

import { ApplicationApi } from "./ApplicationApi";
import { HttpError, HttpStatusCodes } from "./HttpError";

class HttpClient {
  public api: ApplicationApi;
  public client: AxiosInstance;

  private readonly createClient = (api: ApplicationApi): AxiosInstance => {
    const client = axios.create({
      baseURL: api.url,
      timeout: 120000
    });

    client.interceptors.response.use(undefined, (error: any) => {
      if (error.response) {
        if (error.config) {
          if (error.response.status === HttpStatusCodes.Unauthorized) {
            if (api.unauthorizedHandler) {
              api.unauthorizedHandler();
            }
          } else if (error.response.status === HttpStatusCodes.BadRequest) {
            let httpError: HttpError;

            if (typeof error.response.data === "string") {
              httpError = new HttpError(HttpStatusCodes.BadRequest, undefined, error.response.data, error);
            } else {
              httpError = new HttpError(HttpStatusCodes.BadRequest, error.response.data, error.message, error);
            }

            return Promise.reject(httpError);
          }
        }
      }

      return Promise.reject(new HttpError(HttpStatusCodes.ServerError, error, error.message, error));
    });

    client.interceptors.response.use(response => {
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        saveAs(response.data, this.getFilenameFormContentDispositionHeader(disposition));
      }

      return response;
    }, undefined);

    return client;
  };

  private readonly updateCommonHeader = (headerName: string, value: string) => {
    this.client.defaults.headers.common[headerName] = value;
  };

  private readonly getFilenameFormContentDispositionHeader = (disposition: string) => {
    let filename = "";
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);

    if (matches && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }

    return filename;
  };

  public constructor(api: ApplicationApi) {
    this.api = api;
    this.client = this.createClient(api);
  }

  public updateAuthorizationSettings = (accessToken: string) => {
    this.updateCommonHeader("Authorization", this.api.authenticationScheme + " " + accessToken);
  };

  public updateAcceptLanguageSettings = (locale: string) => {
    this.updateCommonHeader("Accept-Language", locale);
  };
}

const instances: HttpClient[] = [];

const updateHttpClient = (callbackFn: (client: HttpClient) => void, affectedApi?: string[]) => {
  let updatingClients = instances;
  if (affectedApi) {
    updatingClients = instances.filter(client => affectedApi.filter(apiName => apiName === client.api.name).length > 0);
  }

  updatingClients.forEach(callbackFn);
};

export const authenticateHttpClient = (accessToken: string, affectedApi?: string[]) => {
  updateHttpClient(httpClient => {
    httpClient.updateAuthorizationSettings(accessToken);
  }, affectedApi);
};

export const applyHttpClientLocale = (locale: string, affectedApi?: string[]) => {
  updateHttpClient(httpClient => {
    httpClient.updateAcceptLanguageSettings(locale);
  }, affectedApi);
};

export const registerHttpClientFor = (api: ApplicationApi) => {
  instances.push(new HttpClient(api));
};

export const getClientFor = (apiName: string, configure?: (client: AxiosInstance) => void): AxiosInstance => {
  const instance = instances.find((e: HttpClient) => e.api.name === apiName);
  let axiosInstance: AxiosInstance;

  if (!instance) {
    throw new Error(`Http client for '${apiName}' is not registered`);
  } else {
    axiosInstance = instance.client;
  }

  configure?.(axiosInstance);

  return axiosInstance;
};
